import { C } from '../components/ui'

/* ════════════════════════════════════════════════════════════════════════════
 *  USE CASES — DevinStratus scenario-driven solution catalogue
 *  ────────────────────────────────────────────────────────────────────────────
 *  5 categories × 4–5 sub-items = 22 enterprise use case deliverables.
 *
 *  Per-category shape:
 *    slug, heading, icon, color, bg
 *    desc      — one-line for header mega-menu
 *    tagline   — short H1 for category page hero
 *    overview  — 2–3 sentence category intro
 *    catStats  — 4 category-credibility numbers
 *    animVariant, animAccent
 *    items[]   — 4–5 sub-items
 *
 *  Per-item shape (use-case-specific):
 *    slug, n, t, tag?, d
 *    hero               — 50–70 word value proposition
 *    scenario           — 2–3 sentence "what's happening" framing (NEW vs Industries)
 *    challenges         — 4 pain points
 *    capabilities       — 4 capability blocks { n, t, d }
 *    features           — 6–8 deliverables
 *    microsoftStack     — 4–5 MS products powering this
 *    benefits           — 4 metric tiles { v, u, l }
 *    timeline           — "X–Y weeks" (NEW vs Industries)
 *    outcomes           — concrete client result sentence
 *    relevantIndustries — array of INDUSTRIES slugs (cross-linking)
 *    relatedSolutions   — array of SOLUTIONS slugs (cross-linking)
 *    process            — 5 delivery steps
 *    faq                — 3 Q&A items
 * ════════════════════════════════════════════════════════════════════════════ */

export const USECASES = [

  /* ════════════ 1. AI ASSISTANTS & COPILOTS ════════════ */
  {
    slug:"ai-assistants-copilots", heading:"AI Assistants & Copilots", icon:"Brain", color:C.teal, bg:C.tealL,
    desc:"Domain-specific AI assistants for every team",
    tagline:"Domain-specific AI assistants that actually deliver — not generic chatbots",
    overview:"Generic LLM chatbots can't answer 'what's our maternity policy' or 'show me last quarter's sales'. We build domain-grounded AI assistants — for HR, IT, sales, knowledge, executives — that cite sources, respect permissions, and integrate with your business systems. Built on Microsoft Copilot Studio, Azure OpenAI, and Azure AI Search.",
    catStats:[
      {v:"5",     l:"Assistant types"},
      {v:"40+",   l:"AI assistants deployed"},
      {v:"<6 wk", l:"Average time to live"},
      {v:"Cited", l:"Every answer sourced"},
    ],
    animVariant:"ai",
    animAccent:"#67e8f9",
    items:[
      {
        slug:"hr-assistant", n:"Users", t:"HR Assistant", tag:"Most Popular",
        d:"Self-service HR queries with policy-cited answers",
        hero:"Replace HR ticket queues with an AI assistant that answers policy questions, processes leave requests, and guides employees through HR self-service. Grounded on your HR policies, integrated with Workday or HiBob, and respectful of employee privacy.",
        scenario:"Your HR team handles hundreds of repetitive policy questions weekly — maternity rules, expense limits, leave balances, sickness policy. Most are answerable from existing documentation but employees can't find it. HR spends time on Tier-1 enquiries instead of strategic work.",
        challenges:[
          "HR team buried under repetitive policy questions",
          "Employees can't find answers in scattered policy documents",
          "Leave/expense requests stuck in email queues",
          "Generic chatbots don't respect employee privacy or grounding",
        ],
        capabilities:[
          { n:"Brain",    t:"Policy-grounded answers",    d:"Answers cite specific policy section, version date, and direct link — verifiable, current, accurate." },
          { n:"Layers",   t:"HRIS integration",            d:"Connects to Workday, SuccessFactors, HiBob, BambooHR — pulls personalised data: leave balance, payslip, training due." },
          { n:"Zap",      t:"Action handling",             d:"Triggers leave requests, expense submissions, manager approvals — not just answers, actual transactions." },
          { n:"Shield",   t:"Privacy by design",           d:"Employees only see their own data; sensitive queries route to humans; all interactions audit-logged for GDPR." },
        ],
        features:[
          "Custom HR assistant in Teams or Outlook",
          "Policy document grounding (Azure AI Search)",
          "Workday / HRIS API integration",
          "Leave request and approval workflows",
          "Expense submission with policy validation",
          "Personalised payslip and benefit summaries",
          "Escalation to human HR with full context",
          "Usage analytics and continuous improvement",
        ],
        microsoftStack:["Microsoft Copilot Studio","Azure OpenAI","Azure AI Search","Microsoft Teams","Power Automate"],
        benefits:[
          {v:"-72%",u:"",l:"Tier-1 HR tickets"},
          {v:"24/7",u:"",l:"Employee self-service"},
          {v:"<3s",u:"",l:"Answer latency"},
          {v:"94%",u:"",l:"Employee satisfaction"},
        ],
        timeline:"5–7 weeks",
        outcomes:"Professional services firm (4,200 employees) cut HR Tier-1 tickets by 72% in 8 weeks — freeing the HR team to focus on talent and culture initiatives.",
        relevantIndustries:["professional-services","financial-services","healthcare"],
        relatedSolutions:["enterprise-copilots","ai-knowledge-assistants","hr-process-automation"],
        process:[
          "HR FAQ inventory & policy audit (Week 1)",
          "Assistant design & grounding scope (Week 2)",
          "Build & integrate HRIS (Weeks 3–5)",
          "Pilot with one department (Week 6)",
          "Org-wide rollout & training (Week 7)",
        ],
        faq:[
          {q:"Will it replace our HR team?",a:"No — it absorbs the repetitive 70% of HR enquiries so your team can focus on the 30% that require human judgement (employee relations, talent strategy, complex cases). HR teams typically expand their strategic impact, not shrink in headcount."},
          {q:"How do you handle sensitive HR topics (grievance, conduct, mental health)?",a:"The assistant detects sensitivity through trained classifiers and routes those queries directly to a human HR partner with the conversation context — never attempting to handle them itself. Every routing decision is configurable."},
          {q:"Does it work in multiple languages?",a:"Yes — Azure OpenAI supports 90+ languages natively. We commonly deploy with English, plus Spanish, French, German, Mandarin, Hindi for multinational clients. The assistant detects language automatically."},
        ],
      },
      {
        slug:"it-support-copilot", n:"Headphones", t:"IT Support Copilot",
        d:"Tier-1 IT support automation with ticket creation",
        hero:"Reset password? Install Adobe? VPN not working? An AI IT support copilot resolves Tier-1 requests in seconds, creates ServiceNow/Cherwell tickets when escalation is needed, and learns from every resolved case. Built into Teams so employees never leave their flow of work.",
        scenario:"Your IT helpdesk handles thousands of tickets monthly — 60–70% are repetitive password resets, software installs, and basic 'how do I' questions. Tier-1 agents burn out on repetition while Tier-2/3 staff wait for cases. Employee experience suffers from long wait times.",
        challenges:[
          "60–70% of IT tickets are basic and repetitive",
          "Employees wait hours for password resets and simple fixes",
          "Tier-1 agents burn out on repetitive resolution",
          "Knowledge from resolved cases doesn't get captured for reuse",
        ],
        capabilities:[
          { n:"Brain",    t:"Self-service resolution", d:"AI guides employees through password resets, software installs, VPN setup — resolving 60%+ without human touch." },
          { n:"Layers",   t:"ITSM integration",         d:"ServiceNow, Cherwell, Jira Service Management — tickets created/updated automatically with full conversation history." },
          { n:"Cpu",      t:"System actions",            d:"Triggers Entra ID password resets, MFA resets, group adds, software deployments via Intune — not just advice, action." },
          { n:"Chart",    t:"Continuous learning",       d:"Successful resolution paths captured automatically; unresolved cases enrich the knowledge base." },
        ],
        features:[
          "AI IT support bot in Teams and Outlook",
          "Password and MFA self-service",
          "Software request and installation workflows",
          "ServiceNow / Cherwell ticket integration",
          "Entra ID and Intune action triggers",
          "Knowledge article auto-generation",
          "Live agent handoff with conversation context",
          "Service desk performance analytics",
        ],
        microsoftStack:["Microsoft Copilot Studio","Azure OpenAI","Microsoft Entra ID","Microsoft Intune","Microsoft Teams"],
        benefits:[
          {v:"-65%",u:"",l:"Tier-1 ticket volume"},
          {v:"<30s",u:"",l:"Password reset time"},
          {v:"24/7",u:"",l:"Self-service support"},
          {v:"-£4.8",u:"/ticket",l:"Avg cost reduction"},
        ],
        timeline:"6–8 weeks",
        outcomes:"Global consulting firm cut Tier-1 IT ticket volume by 65% in 12 weeks — recovering £1.9M annually in service desk operating costs and reducing employee wait times from hours to seconds.",
        relevantIndustries:["professional-services","financial-services","manufacturing"],
        relatedSolutions:["enterprise-copilots","ai-knowledge-assistants","workflow-automation"],
        process:[
          "ITSM ticket pattern analysis (Week 1)",
          "Resolution workflow design (Weeks 2–3)",
          "Build & integrate ITSM, Entra, Intune (Weeks 4–6)",
          "Pilot with IT team (Week 7)",
          "Org-wide deployment (Week 8)",
        ],
        faq:[
          {q:"Will it integrate with our existing ITSM (ServiceNow, Cherwell, Jira)?",a:"Yes — we integrate with all major ITSM platforms via REST APIs. Tickets created by the copilot appear identically to human-created tickets, with full conversation context attached, so Tier-2 agents have complete history."},
          {q:"How does it handle escalation when it can't resolve?",a:"After 2 unsuccessful resolution attempts or when the user asks for human help, the copilot creates a ServiceNow ticket with the full conversation, suspected category, and attempted fixes — then notifies the user. No information is lost in transition."},
          {q:"Can it actually reset passwords / install software, or just give instructions?",a:"It actually performs the actions. Entra ID password resets, MFA resets, Intune software deployments — these run as authenticated system actions on the user's behalf, properly logged for audit."},
        ],
      },
      {
        slug:"knowledge-assistant", n:"BookOpen", t:"Knowledge Assistant",
        d:"Enterprise knowledge search with cited answers",
        hero:"Your organisation's collective knowledge lives in SharePoint, Teams, OneDrive, internal wikis, and product docs — and is impossible to find when you need it. A knowledge assistant grounds GPT-4 on your entire corpus with permission awareness, citing specific documents on every answer. Built on Azure AI Search.",
        scenario:"Your subject matter experts spend hours each week answering questions whose answers exist somewhere in your documents. New joiners take weeks finding precedents, methodologies, templates. Critical organisational knowledge walks out the door when experts retire.",
        challenges:[
          "Knowledge fragmented across SharePoint, Teams, OneDrive, wikis",
          "Same questions repeatedly answered by senior staff",
          "New joiners take months to find precedents and templates",
          "Critical knowledge walks out the door when experts leave",
        ],
        capabilities:[
          { n:"Brain",    t:"Grounded AI search",       d:"GPT-4 grounded on your corpus — answers cite specific documents, sections, versions; no hallucination." },
          { n:"Shield",   t:"Permission-aware",          d:"Respects SharePoint and Teams permissions; users only see results they're authorised to access." },
          { n:"Layers",   t:"Multi-source indexing",     d:"SharePoint, Teams, OneDrive, Confluence, ServiceNow, custom DBs — unified in one searchable index." },
          { n:"Chart",    t:"Search analytics",          d:"Track unanswered queries to surface content gaps; identify trending topics; measure adoption." },
        ],
        features:[
          "Custom knowledge assistant in Teams / Outlook / web",
          "Multi-source content indexing",
          "Permission-aware search (respects ACLs)",
          "Cited answers with source links and confidence scores",
          "Conversational follow-up questions",
          "Voice query support",
          "Unanswered question tracking",
          "Adoption and content gap analytics",
        ],
        microsoftStack:["Azure AI Search","Azure OpenAI","Microsoft Copilot Studio","SharePoint Online","Microsoft Entra ID"],
        benefits:[
          {v:"3×",u:"",l:"Faster new-hire ramp"},
          {v:"-58%",u:"",l:"Time finding info"},
          {v:"Cited",u:"",l:"Every answer sourced"},
          {v:"ACL",u:"-aware",l:"Permission respect"},
        ],
        timeline:"4–6 weeks",
        outcomes:"Law firm cut precedent-search time from 38 to 9 minutes — recovering ~£3.4M of fee-earner time annually with verifiable, cited answers from their case archive.",
        relevantIndustries:["professional-services","healthcare","financial-services"],
        relatedSolutions:["ai-knowledge-assistants","knowledge-management","enterprise-copilots"],
        process:[
          "Content source inventory & strategy (Week 1)",
          "Indexing architecture & permissions design (Week 2)",
          "Content ingestion & embedding (Weeks 3–4)",
          "Assistant build & UI integration (Week 5)",
          "Pilot, refinement, rollout (Week 6)",
        ],
        faq:[
          {q:"How does it handle confidential or privileged content?",a:"Azure AI Search uses your existing SharePoint and Teams permission ACLs at query time — a user only sees results from documents they're authorised to read. Privileged matter content, confidential HR files, executive-only documents stay confidential."},
          {q:"What about content in non-Microsoft systems (Confluence, ServiceNow, Salesforce)?",a:"We index any system with an API. Common integrations: Confluence, ServiceNow, Salesforce, NetSuite, Workday, Box, Dropbox. The assistant searches everything in one query, respecting each system's permissions."},
          {q:"How fresh is the content?",a:"Configurable — typically every 4–6 hours for SharePoint/Teams (most clients' standard) or near-realtime via change feeds for critical sources. Stale content is the #1 trust killer in knowledge systems, so we prioritise refresh design."},
        ],
      },
      {
        slug:"sales-assistant", n:"TrendUp", t:"Sales Assistant",
        d:"AI deal research, account briefings & follow-ups",
        hero:"Replace pre-meeting Google searches and post-meeting note-taking with an AI sales assistant. Account research synthesised from web + CRM + news; meeting briefings auto-generated; follow-up drafts ready for review; pipeline analytics in natural language. Built on Dynamics 365 Sales + Azure OpenAI.",
        scenario:"Your sales team spends hours preparing for each meeting — researching the prospect, pulling CRM history, reviewing recent news. Then more hours after writing follow-up emails and updating CRM. Senior sellers do this OK; junior reps struggle and deals slip.",
        challenges:[
          "Pre-meeting prep takes hours per opportunity",
          "Post-meeting follow-up is inconsistent across the team",
          "CRM updates happen days late (or never)",
          "Junior reps lack senior-level account intelligence",
        ],
        capabilities:[
          { n:"Brain",    t:"Account intelligence",      d:"Pre-meeting brief: company changes, news, executive moves, financial signals, prior touchpoints — all summarised." },
          { n:"FileText", t:"Follow-up drafts",           d:"AI generates first-draft follow-up emails referencing meeting notes, next steps, materials — seller approves and sends." },
          { n:"Layers",   t:"CRM auto-update",            d:"Activity logging, contact updates, opportunity progression — captured automatically from email and meetings." },
          { n:"Chart",    t:"Pipeline insights",          d:"Ask 'what's at risk this quarter' in natural language; get prioritised risk list with reasoning." },
        ],
        features:[
          "Pre-meeting account brief generation",
          "Company news, financial, and people change tracking",
          "CRM activity auto-logging from email and Teams",
          "Follow-up email drafting with context",
          "Next-best-action recommendations",
          "Natural-language pipeline queries",
          "Opportunity risk flagging",
          "Win/loss pattern analysis",
        ],
        microsoftStack:["Dynamics 365 Sales","Microsoft Sales Copilot","Azure OpenAI","Microsoft Outlook","Power BI"],
        benefits:[
          {v:"-3 hrs",u:"/day",l:"Saved per seller"},
          {v:"+18%",u:"",l:"Quota attainment"},
          {v:"+34%",u:"",l:"CRM data quality"},
          {v:"Same-day",u:"",l:"Follow-up rate"},
        ],
        timeline:"6–8 weeks",
        outcomes:"B2B SaaS firm (180 sellers) saved 3 hours per seller daily and lifted quota attainment 18 points — translating to £6.2M additional annual revenue with no headcount addition.",
        relevantIndustries:["professional-services","financial-services","manufacturing"],
        relatedSolutions:["enterprise-copilots","customer-experience-apps","predictive-insights"],
        process:[
          "Sales process audit & pain analysis (Week 1)",
          "CRM integration scope & data quality plan (Week 2)",
          "Build & integrate D365 Sales, Outlook (Weeks 3–5)",
          "Pilot with one sales pod (Weeks 6–7)",
          "Team-wide rollout & enablement (Week 8)",
        ],
        faq:[
          {q:"How does it work with our existing CRM (Salesforce, HubSpot)?",a:"We integrate with Salesforce, HubSpot, Pipedrive, and others via their APIs — though deepest experience is on D365 Sales. The assistant works as an overlay; your CRM remains the system of record."},
          {q:"Does the AI actually write good follow-up emails?",a:"It writes good first drafts — referencing meeting notes, prior touchpoints, agreed next steps. Sellers always review and edit before sending. Most clients see 80%+ of drafts go out with minor edits."},
          {q:"Will it replace our SDR/BDR team?",a:"No — it makes them more effective. SDRs focused on outreach and qualification get more done per day; AEs spend less time on admin and more on selling. Most clients reduce SDR-to-AE handoff friction rather than reduce headcount."},
        ],
      },
      {
        slug:"executive-insights-copilot", n:"Brain", t:"Executive Insights Copilot",
        d:"Natural-language analytics for the C-suite",
        hero:"Replace 'can you pull this report' requests with executives asking questions directly in natural language. 'How are we tracking vs plan in EMEA?' or 'What's driving last week's churn spike?' — answered with charts, narrative, and source citations. Built on Microsoft Fabric + Copilot for Power BI.",
        scenario:"Your executives need answers fast but don't navigate dashboards. They email finance for ad-hoc analysis, wait days for responses, and miss the moment when action mattered. Meanwhile your analytics team is buried in one-off requests instead of strategic work.",
        challenges:[
          "Executives can't navigate complex BI dashboards",
          "Ad-hoc analysis requests overload the analytics team",
          "By the time reports arrive, the decision moment has passed",
          "Insights live in dashboards but narratives don't accompany them",
        ],
        capabilities:[
          { n:"Brain",    t:"Natural-language Q&A",     d:"Executives ask in plain English: 'Show me YoY revenue by region with commentary' — get answer + chart + narrative." },
          { n:"Chart",    t:"AI-generated commentary",   d:"GPT-4 writes 'what's changed, why, what to watch' narrative tailored to executive audience." },
          { n:"Layers",   t:"Unified semantic model",   d:"One Fabric model spans finance, sales, ops, HR — executives ask cross-functional questions without IT involvement." },
          { n:"Shield",   t:"Row-level security",       d:"Regional execs see their region; CFO sees everything; respects your existing data access policies." },
        ],
        features:[
          "Natural-language query interface",
          "AI-generated executive commentary",
          "Mobile-first executive briefings (Teams)",
          "Scheduled morning briefing summaries",
          "Anomaly and exception detection",
          "Cross-functional unified data model",
          "Row-level security and access control",
          "Voice query support",
        ],
        microsoftStack:["Microsoft Fabric","Power BI","Copilot for Power BI","Azure OpenAI","Microsoft Teams"],
        benefits:[
          {v:"<10s",u:"",l:"Question to answer"},
          {v:"-70%",u:"",l:"Ad-hoc analytics requests"},
          {v:"Real-time",u:"",l:"Executive intelligence"},
          {v:"AI",u:"-narrated",l:"Auto-commentary"},
        ],
        timeline:"6–9 weeks",
        outcomes:"FTSE 250 CEO cut ad-hoc analytics requests by 70% by enabling natural-language exploration — the analytics team redirected 1,400 hours/year to strategic forecasting work.",
        relevantIndustries:["financial-services","manufacturing","retail-ecommerce"],
        relatedSolutions:["executive-dashboards","microsoft-fabric","enterprise-copilots"],
        process:[
          "Executive needs workshop (Week 1)",
          "Semantic model architecture (Weeks 2–3)",
          "Fabric implementation & data modelling (Weeks 4–6)",
          "Copilot grounding & briefing design (Weeks 7–8)",
          "Executive pilot & rollout (Week 9)",
        ],
        faq:[
          {q:"Our data isn't clean — can we still do this?",a:"Honest answer: data quality directly drives Copilot quality. We assess data readiness in Week 1 and recommend a path — sometimes Fabric remediation is part of the project; sometimes we pilot on a clean subset first. We won't deploy a Copilot on bad data because it embarrasses everyone."},
          {q:"How do you stop the AI from making things up?",a:"Power BI Copilot is grounded on your semantic model — it can only answer using your actual data and metric definitions. It won't fabricate. If a question can't be answered from data, it says so clearly."},
          {q:"Will the C-suite actually use it?",a:"Adoption depends on (a) data quality, (b) executive sponsorship, (c) mobile-first design. We co-design with 3–5 executives upfront. When done right, 70–90% of executive briefings come through the Copilot within 60 days."},
        ],
      },
    ],
  },


  /* ════════════ 2. INTELLIGENT WORKFLOWS ════════════ */
  {
    slug:"intelligent-workflows", heading:"Intelligent Workflows", icon:"Zap", color:C.blue, bg:C.blueL,
    desc:"Automate approvals, requests & internal processes",
    tagline:"Stop running your business on email approvals and Excel trackers",
    overview:"Procurement approvals, leave requests, invoice processing, contract sign-offs, employee onboarding — when these run on email and spreadsheets, things break. We build intelligent workflows on Power Automate and Dynamics 365 that route by policy, escalate by SLA, capture every approval for audit, and integrate with your ERP/CRM/HR systems.",
    catStats:[
      {v:"5",     l:"Workflow types"},
      {v:"60+",   l:"Workflows deployed"},
      {v:"<5 wk", l:"Average build time"},
      {v:"Audit", l:"-ready trails"},
    ],
    animVariant:"automation",
    animAccent:"#67e8f9",
    items:[
      {
        slug:"procurement-approvals", n:"Cart", t:"Procurement Approvals", tag:"Most Popular",
        d:"PO routing with budget, vendor & policy validation",
        hero:"Replace email PO approvals with policy-aware workflows: amount-based routing, budget validation against ERP, vendor compliance checks, mobile sign-off, full audit trails. From 'approve please?' chains to fully audited, SLA-managed approvals — without disrupting your existing ERP.",
        scenario:"Procurement requests live in email — forwarded, lost, approved out of policy, double-signed. Budget checking happens after spend has already occurred. Audit prep is a nightmare. Meanwhile vendors wait days for POs and your buying team chases approvals daily.",
        challenges:[
          "PO approvals stuck in email chains for days",
          "Budget validation happens after the fact",
          "Vendor compliance checks done inconsistently",
          "Audit trail reconstruction is manual and painful",
        ],
        capabilities:[
          { n:"Layers",   t:"Policy-driven routing",   d:"Amount thresholds, cost-centre, category-based approval chains — encoded in workflow, not in people's heads." },
          { n:"Calc",     t:"Budget validation",        d:"Real-time budget check against ERP (D365, SAP, Oracle, NetSuite) — block or warn on over-budget requests." },
          { n:"Shield",   t:"Vendor compliance",        d:"KYC, sanctions, contract status, insurance currency — checked at request, not after." },
          { n:"Chart",    t:"Spend visibility",          d:"Real-time committed vs actual spend by category, cost-centre, vendor — for FP&A and procurement leaders." },
        ],
        features:[
          "Mobile PO submission and approval",
          "Multi-stage routing (amount, category, budget)",
          "Real-time ERP budget checks",
          "Vendor compliance validation",
          "Out-of-office delegation",
          "SLA tracking with auto-escalation",
          "Spend dashboards by category and cost-centre",
          "Integration with D365, SAP, Oracle, NetSuite",
        ],
        microsoftStack:["Power Automate","Power Apps","Dynamics 365 Finance","Microsoft Teams","Power BI"],
        benefits:[
          {v:"-78%",u:"",l:"Approval cycle time"},
          {v:"100%",u:"",l:"Policy compliance"},
          {v:"-£420k",u:"/yr",l:"Off-contract spend"},
          {v:"Real-time",u:"",l:"Budget visibility"},
        ],
        timeline:"4–6 weeks",
        outcomes:"Manufacturing group cut PO approval cycle from 7 days to 1.5 days across 14 entities — and identified £420k of off-contract spend in the first quarter through real-time policy enforcement.",
        relevantIndustries:["manufacturing","retail-ecommerce","logistics-supply-chain"],
        relatedSolutions:["procurement-automation","approval-automation","workflow-automation"],
        process:[
          "Procurement policy & approval matrix audit (Week 1)",
          "Workflow design with finance sign-off (Week 2)",
          "Build & integrate ERP (Weeks 3–4)",
          "Pilot with one entity / cost centre (Week 5)",
          "Multi-entity rollout (Week 6)",
        ],
        faq:[
          {q:"Will this replace our ERP procurement module?",a:"No — your ERP remains the system of record for POs, receipts, invoices. We orchestrate the approval workflow that happens BEFORE the PO is created in ERP. Approved requests flow into ERP cleanly with full context attached."},
          {q:"Can it handle multi-currency / multi-entity?",a:"Yes — workflows have branching for currency, entity, country regulations. We routinely deploy across 10–20 entities with localised approval matrices and currency-aware thresholds."},
          {q:"How does it work with our existing P2P platform (Coupa, Ariba)?",a:"We complement P2P platforms by adding richer approval orchestration that P2P tools often don't address well — especially complex multi-stage approvals, budget integration, and mobile approval experiences."},
        ],
      },
      {
        slug:"leave-management", n:"Calc", t:"Leave Management",
        d:"Self-service leave with manager workflows",
        hero:"Annual leave, sick days, parental leave, study leave — submitted on phone in seconds, routed to managers with team-coverage visibility, tracked against entitlement, integrated with payroll. Replace email-based leave with self-service that employees actually love using.",
        scenario:"Employees submit leave via email or paper forms — managers approve in inbox, HR transcribes to spreadsheets, payroll gets late updates. Leave balances are unclear. Team coverage conflicts happen because no one sees the whole picture. New starters add weeks of admin.",
        challenges:[
          "Leave requests submitted via email, forms, or spreadsheets",
          "Managers don't see team-wide coverage when approving",
          "Leave balances unclear; employees forget entitlement",
          "Payroll integration is manual and error-prone",
        ],
        capabilities:[
          { n:"Cpu",      t:"Mobile self-service",      d:"Request leave from phone in 15 seconds — type, dates, comments. Status visible in real time." },
          { n:"Users",    t:"Team coverage view",        d:"Managers see team availability when approving — avoiding conflicts and ensuring coverage." },
          { n:"Chart",    t:"Balance tracking",          d:"Real-time entitlement, accrual, carryover — visible to employee and manager. No surprises at year-end." },
          { n:"Layers",   t:"Payroll integration",       d:"Approved leave flows to payroll (ADP, Workday, SAP) automatically — no spreadsheet handoff." },
        ],
        features:[
          "Mobile leave request app",
          "Multi-type leave (annual, sick, parental, study, unpaid)",
          "Manager approval workflows with team view",
          "Real-time balance and accrual tracking",
          "Holiday calendar with public holidays",
          "Sickness recording with return-to-work prompts",
          "Payroll system integration",
          "Compliance with country-specific leave laws",
        ],
        microsoftStack:["Power Apps","Power Automate","Microsoft Teams","SharePoint Online","Dataverse"],
        benefits:[
          {v:"15s",u:"",l:"Avg request time"},
          {v:"-92%",u:"",l:"HR admin work"},
          {v:"Real-time",u:"",l:"Balance visibility"},
          {v:"Zero",u:"",l:"Payroll errors"},
        ],
        timeline:"3–4 weeks",
        outcomes:"Mid-sized retailer (2,100 employees) cut HR leave admin by 92% and eliminated payroll discrepancies — saving 24 HR hours per week previously spent on leave reconciliation.",
        relevantIndustries:["retail-ecommerce","professional-services","manufacturing"],
        relatedSolutions:["hr-process-automation","employee-self-service","workflow-automation"],
        process:[
          "Leave policy & entitlement audit (Week 1)",
          "Workflow design & payroll integration plan (Week 2)",
          "Build & integrate payroll system (Week 3)",
          "Pilot with one department (Week 3–4)",
          "Org-wide rollout & training (Week 4)",
        ],
        faq:[
          {q:"Will this replace our HRIS leave module (Workday, SuccessFactors)?",a:"We can either replace it (cheaper, more flexible) or integrate with it (HRIS remains source of truth). Depends on your situation — we'll recommend the right path during discovery."},
          {q:"What about complex leave types like sabbaticals, bereavement, jury duty?",a:"All configurable — you define the leave types, eligibility rules, approval chains, and balance calculations. We've deployed setups handling 15+ leave types across multiple countries."},
          {q:"Does it work for shift workers / hourly employees?",a:"Yes — we support hourly leave (1.5 hours, etc.), shift-aware balance calculations, and minimum-notice rules per role family. Common in retail, healthcare, manufacturing deployments."},
        ],
      },
      {
        slug:"invoice-processing", n:"FileText", t:"Invoice Processing",
        d:"AI invoice capture, matching & approval",
        hero:"Replace AP team's manual invoice keying with AI-powered capture, 3-way matching against PO and receipt, exception routing, and automated posting to ERP. Cut invoice processing cost from £8 to £1.50 per invoice — and pay vendors on time without losing early-payment discounts.",
        scenario:"Invoices arrive by email, PDF, paper — AP keys them manually, matches to POs in Excel, chases approvals for non-PO invoices. Exception rates are high; payment delays hurt vendor relationships; early-payment discounts get missed; AP team is constantly overwhelmed.",
        challenges:[
          "Manual invoice keying takes 15+ minutes per invoice",
          "PO matching done in Excel — error-prone and slow",
          "Non-PO invoice approvals stuck in email",
          "Early-payment discounts missed because processing too slow",
        ],
        capabilities:[
          { n:"Brain",    t:"AI invoice capture",       d:"Azure Document Intelligence extracts line items, totals, tax, dates from PDFs, emails, scans — 98%+ accuracy." },
          { n:"Layers",   t:"3-way matching",            d:"Auto-match invoice to PO and receipt; exceptions routed to AP for resolution; clean matches auto-post to ERP." },
          { n:"Zap",      t:"Approval routing",          d:"Non-PO invoices routed by amount, cost-centre, category — mobile approvals from Teams or Outlook." },
          { n:"Chart",    t:"AP analytics",              d:"DPO, discount capture, cycle time, exception patterns — for AP leaders to drive continuous improvement." },
        ],
        features:[
          "AI invoice capture from email/PDF/scan",
          "Automatic 3-way matching (invoice/PO/receipt)",
          "Exception routing and resolution workflows",
          "Non-PO invoice approval workflows",
          "Tax validation and compliance checks",
          "Early-payment discount tracking",
          "Vendor self-service portal for invoice status",
          "ERP integration (D365, SAP, Oracle, NetSuite)",
        ],
        microsoftStack:["Azure AI Document Intelligence","Power Automate","Dynamics 365 Finance","Power Apps","Power BI"],
        benefits:[
          {v:"-81%",u:"",l:"Processing cost"},
          {v:"<1.5 day",u:"",l:"Invoice to posting"},
          {v:"96%",u:"",l:"Touchless processing"},
          {v:"+£280k",u:"/yr",l:"Discount capture"},
        ],
        timeline:"5–7 weeks",
        outcomes:"Logistics group (180k invoices/year) reduced processing cost from £8 to £1.50 per invoice and captured £280k more in early-payment discounts annually — net £1.5M operational improvement.",
        relevantIndustries:["logistics-supply-chain","manufacturing","retail-ecommerce"],
        relatedSolutions:["ai-document-intelligence","finance-automation","approval-automation"],
        process:[
          "AP process audit & exception analysis (Week 1)",
          "Document model training & ERP integration design (Weeks 2–3)",
          "Build workflows & train AI models (Weeks 4–5)",
          "Pilot with one entity / vendor group (Week 6)",
          "Full rollout & vendor portal launch (Week 7)",
        ],
        faq:[
          {q:"How accurate is the AI invoice capture?",a:"For standard invoice formats, 98%+ accuracy on line items, totals, dates. For unusual formats (handwritten, poor scans, complex tables), accuracy is lower — we configure confidence thresholds that route uncertain items to human review."},
          {q:"What about VAT / GST / sales tax compliance?",a:"Built in — tax rates, codes, and rules per country. We deploy regularly across UK VAT, EU VAT, US sales tax, Indian GST, with country-specific compliance rules and reporting."},
          {q:"Can it handle our supplier diversity (large + small + occasional vendors)?",a:"Yes — large vendors typically use the portal (electronic invoicing). Small/occasional vendors continue email/PDF; AI capture handles those. Both flow into the same processing pipeline."},
        ],
      },
      {
        slug:"contract-approvals", n:"Shield", t:"Contract Approvals",
        d:"Multi-stakeholder contract review & sign-off",
        hero:"Legal, procurement, finance, security, and business owner all need to review contracts — typically in parallel email threads with redlines flying everywhere. We orchestrate contract approval workflows with version control, parallel reviews, audit trails, and AI-assisted clause analysis. From quote to signed contract in days, not weeks.",
        scenario:"Contracts ping-pong between legal, procurement, finance, and business owners — each adding redlines, each waiting on the others. Versions get confused; insurance clauses missed; SLAs not pinned down; deal owner chases everyone weekly. Sales cycles slip; vendor relationships strain.",
        challenges:[
          "Contract review takes 4+ weeks because reviewers work sequentially",
          "Version control breaks down across email redlines",
          "Insurance, SLA, indemnity clauses inconsistently reviewed",
          "Audit trail of who approved what is impossible to reconstruct",
        ],
        capabilities:[
          { n:"Layers",   t:"Parallel review",          d:"Legal, procurement, finance, security review in parallel — not sequentially — with consolidated commenting." },
          { n:"Brain",    t:"AI clause analysis",        d:"Identify risky clauses, missing standard language, deviations from preferred terms — flagged at intake." },
          { n:"FileText", t:"Version control",            d:"Single source of truth; redline tracking; every version archived with reviewer, comments, decision." },
          { n:"Shield",   t:"Audit & e-signature",        d:"Full approval chain logged; integration with DocuSign / Adobe Sign; final executed contract archived." },
        ],
        features:[
          "Contract intake with metadata capture",
          "Parallel stakeholder review workflows",
          "AI clause extraction and risk flagging",
          "Version control with redline tracking",
          "Comment threading by clause",
          "Approval matrix by contract value / type",
          "E-signature integration (DocuSign / Adobe Sign)",
          "Contract repository with renewal alerts",
        ],
        microsoftStack:["Power Automate","SharePoint Online","Azure AI Document Intelligence","Microsoft Teams","Dataverse"],
        benefits:[
          {v:"-65%",u:"",l:"Approval cycle time"},
          {v:"100%",u:"",l:"Version control"},
          {v:"AI",u:"-flagged",l:"Risk clauses"},
          {v:"Audit",u:"-ready",l:"Approval trails"},
        ],
        timeline:"5–7 weeks",
        outcomes:"Technology firm cut contract approval cycle from 24 days to 8 days across 2,400 contracts/year — accelerating revenue recognition by an average of 16 days and improving NRR by 4 points.",
        relevantIndustries:["professional-services","financial-services","manufacturing"],
        relatedSolutions:["approval-automation","ai-document-intelligence","document-management"],
        process:[
          "Contract process audit & taxonomy (Week 1)",
          "Workflow design & approval matrix (Week 2)",
          "Build workflows & AI model training (Weeks 3–5)",
          "Pilot with legal & procurement (Week 6)",
          "Org-wide rollout (Week 7)",
        ],
        faq:[
          {q:"Will this replace our CLM platform (Ironclad, Agiloft, Icertis)?",a:"For organisations with established CLM, we integrate around it rather than replace it. For organisations without dedicated CLM, our solution often delivers equivalent capability at lower cost. We'll assess fit honestly."},
          {q:"How does AI clause analysis work?",a:"Azure AI Document Intelligence is trained on your contract corpus to identify your standard clauses, deviations, and risky language patterns. Custom models for your specific contract types. Identifies what humans review for, so reviewers prioritise."},
          {q:"What about NDA / order forms / quick-turn contracts?",a:"We define multiple workflow types — full master service agreements get full review; NDAs get fast-track with just legal; order forms auto-approve under defined parameters. Routes by contract type."},
        ],
      },
      {
        slug:"employee-onboarding", n:"User", t:"Employee Onboarding",
        d:"Day-1 productive new hires with full provisioning",
        hero:"From offer-accepted to day-1 productive: kit ordering, IT provisioning, manager intro, security training, system access, payroll setup — orchestrated from HR through IT, finance, facilities. Every new hire experience consistent, complete, mobile-friendly. New starter ramp time cut in half.",
        scenario:"Hiring is hard and you finally close the candidate. Then onboarding falls apart: IT kit is late, system access takes weeks, manager isn't briefed, security training overdue, day-1 confusion. New starter contemplates leaving in first month; productivity impact lasts a quarter.",
        challenges:[
          "Day-1 chaos: missing IT, no manager intro, no system access",
          "HR, IT, facilities, finance work in silos and miss handoffs",
          "Mandatory training assignments tracked manually",
          "New starter ramp time of 3+ weeks because of admin friction",
        ],
        capabilities:[
          { n:"Layers",   t:"Cross-function orchestration", d:"HR, IT, finance, facilities, manager — coordinated automatically from offer-accepted event." },
          { n:"Cpu",      t:"IT provisioning",            d:"Entra ID account, M365, devices, security groups, app access — automated from joiner record." },
          { n:"User",     t:"New starter portal",          d:"Personalised mobile portal: tasks, training, manager intro, FAQ, kit delivery status." },
          { n:"Chart",    t:"Onboarding analytics",        d:"Time-to-productive, 90-day attrition, completion rates by manager — for HR leaders to drive improvement." },
        ],
        features:[
          "Offer-accepted to day-1 task automation",
          "Mobile new-starter experience portal",
          "Entra ID and M365 provisioning",
          "Device ordering and tracking",
          "Manager briefing and team intro workflows",
          "Mandatory training assignment and tracking",
          "30/60/90-day check-in workflows",
          "Multi-country onboarding support",
        ],
        microsoftStack:["Power Apps","Power Automate","Microsoft Entra ID","Microsoft Intune","Microsoft Teams"],
        benefits:[
          {v:"Day-1",u:"",l:"Productive new hires"},
          {v:"-58%",u:"",l:"Onboarding admin time"},
          {v:"-23%",u:"",l:"90-day attrition"},
          {v:"100%",u:"",l:"Training compliance"},
        ],
        timeline:"5–7 weeks",
        outcomes:"Financial services firm (380 hires/year) cut new-starter ramp from 22 to 8 days and reduced 90-day attrition by 23% — saving ~£1.4M annually in productivity loss and replacement costs.",
        relevantIndustries:["financial-services","professional-services","healthcare"],
        relatedSolutions:["hr-process-automation","workflow-automation","collaboration-automation"],
        process:[
          "Onboarding process audit & pain mapping (Week 1)",
          "Cross-functional workflow design (Weeks 2–3)",
          "Build & integrate HRIS / Entra ID / Intune (Weeks 4–5)",
          "Pilot with one practice / department (Week 6)",
          "Org-wide rollout & manager training (Week 7)",
        ],
        faq:[
          {q:"How does it work with our HRIS (Workday, BambooHR)?",a:"HRIS remains source of truth for the joiner record. We orchestrate the cross-functional tasks AROUND the HRIS record — IT provisioning, manager actions, training assignments, etc. Most HRIS systems do this poorly natively."},
          {q:"Can we customise per practice / region / role?",a:"Yes — onboarding journeys branch by role family, country, practice area, seniority. Most professional services and consulting clients run 8–20 variant flows within a single platform."},
          {q:"What about contractors and contingent workers?",a:"Different workflows for contractor onboarding (lighter touch, different compliance, time-bound access). Often handled by a separate but parallel flow design."},
        ],
      },
    ],
  },


  /* ════════════ 3. ENTERPRISE APPLICATIONS ════════════ */
  {
    slug:"enterprise-applications", heading:"Enterprise Applications", icon:"Layers", color:C.purple, bg:C.purpleL,
    desc:"Custom apps for operations, field & service",
    tagline:"Replace spreadsheets and paper forms with mobile-first enterprise apps",
    overview:"Internal operations apps, inspection apps, asset management, service request platforms — built on Power Apps and Dynamics 365. Mobile-first, offline-capable, integrated with your back-office systems. Replace shadow IT spreadsheets and paper forms with proper enterprise applications, delivered in weeks not years.",
    catStats:[
      {v:"4",     l:"App categories"},
      {v:"80+",   l:"Apps deployed"},
      {v:"<8 wk", l:"Average time to live"},
      {v:"Mobile", l:"-first design"},
    ],
    animVariant:"apps",
    animAccent:"#a5b4fc",
    items:[
      {
        slug:"internal-operations-apps", n:"Cpu", t:"Internal Operations Apps", tag:"Most Popular",
        d:"Custom operational apps for any team",
        hero:"Every team has a process running on a spreadsheet that shouldn't be. Project tracking, customer onboarding, vendor management, daily ops — we build proper mobile-first applications in 4-8 weeks. Replace shadow IT with governed, scalable, integrated enterprise apps your team actually uses.",
        scenario:"You discover that the warehouse manager runs operations from a 30-tab Excel; the project office tracks all projects in a Smartsheet only one person edits; the office manager logs requests in an outdated Access database. Every team has its 'critical spreadsheet' — undocumented, unreliable, ungoverned.",
        challenges:[
          "Mission-critical processes run on personal Excel files",
          "No version control, no mobile access, no integration",
          "When the 'spreadsheet owner' leaves, knowledge walks out",
          "IT can't keep up with backlog of departmental app requests",
        ],
        capabilities:[
          { n:"Cpu",      t:"Mobile-first design",      d:"Every app responsive and mobile-optimised — used in field, warehouse, on the road, in meetings." },
          { n:"Layers",   t:"Backend integration",       d:"Connects to your ERP, CRM, HRIS, SharePoint — single source of truth, multiple front-end experiences." },
          { n:"Shield",   t:"Governance built-in",       d:"User access, audit logging, environment management, ALM — enterprise governance without dev team overhead." },
          { n:"Zap",      t:"Rapid delivery",            d:"4–8 week delivery cycles — far faster than custom dev, far more governed than 'someone's spreadsheet'." },
        ],
        features:[
          "Custom mobile apps for any operational process",
          "Offline-capable for field/warehouse environments",
          "Integration with ERP, CRM, HRIS, SharePoint",
          "Power BI dashboards embedded in apps",
          "Photo capture and file attachments",
          "Approval workflows and notifications",
          "Role-based access and security",
          "ALM and DevOps for managed lifecycle",
        ],
        microsoftStack:["Power Apps","Power Automate","Dataverse","Power BI","Microsoft Entra ID"],
        benefits:[
          {v:"4–8 wk",u:"",l:"Per-app delivery"},
          {v:"-65%",u:"",l:"vs custom dev cost"},
          {v:"Mobile",u:"",l:"-first by default"},
          {v:"Enterprise",u:"",l:"governance"},
        ],
        timeline:"4–8 weeks per app",
        outcomes:"Manufacturing group deployed 14 operational apps in 18 months — replacing critical spreadsheets across operations, quality, HR, and engineering with proper apps used daily by 1,800+ employees.",
        relevantIndustries:["manufacturing","logistics-supply-chain","retail-ecommerce"],
        relatedSolutions:["enterprise-power-apps","workflow-automation","operations-management"],
        process:[
          "Process discovery & app prioritisation (Week 1)",
          "UX design & data model (Week 2)",
          "Build, integrate, test (Weeks 3–6)",
          "User pilot (Week 7)",
          "Launch & training (Week 8)",
        ],
        faq:[
          {q:"What's the difference between Power Apps and custom dev?",a:"Power Apps gives 80% of the capability at 30% of the cost and 25% of the time, for the vast majority of internal apps. Custom dev still wins for highly specialised UX, real-time gaming, or unique tech stacks. We'll be honest about which fits."},
          {q:"Can citizen developers maintain these apps after you build them?",a:"That's the point. We design with ALM, governance, and citizen-developer hand-off in mind. Most clients have business power users handling 60–70% of post-launch enhancements after a 2–4 week handover programme."},
          {q:"What about apps that need to scale to 10k+ users or massive data?",a:"Power Apps scales well — we've deployed apps to 30k users with Dataverse handling tens of millions of records. For extreme scale (100k+ concurrent), we recommend specific architecture patterns or hybrid approaches."},
        ],
      },
      {
        slug:"inspection-apps", n:"CheckCircle", t:"Inspection Apps",
        d:"Mobile audits, checklists & site inspections",
        hero:"Quality inspections, safety audits, site surveys, equipment checks — replace clipboards and paper with mobile inspection apps. Offline-capable for remote sites, photo evidence, GPS-tagged, instantly synced to head office. From inspection to action plan in hours, not weeks.",
        scenario:"Inspectors and auditors work with paper checklists in factories, sites, stores, vehicles — then spend hours back in the office transcribing into Excel. Photos and evidence get lost. Action items languish in inboxes. Audit findings are days old before management sees them.",
        challenges:[
          "Paper-based inspections lose data and slow action",
          "Photo and evidence capture is awkward and inconsistent",
          "Findings transcribed manually back at the office",
          "Action items get lost; root cause analysis is impossible",
        ],
        capabilities:[
          { n:"Cpu",      t:"Mobile inspection",         d:"Offline-capable mobile app for warehouses, factories, vehicles, remote sites — works without connectivity." },
          { n:"FileText", t:"Photo & GPS evidence",       d:"Capture photos, annotations, GPS location, signatures — automatically attached to inspection record." },
          { n:"Zap",      t:"Action workflows",           d:"Findings auto-route to responsible owner with severity-based SLA; tracked through to closure." },
          { n:"Chart",    t:"Compliance analytics",       d:"Trend analysis by site, type, severity — surfacing systemic issues before they become audit findings." },
        ],
        features:[
          "Mobile inspection app (iOS, Android)",
          "Offline data capture with auto-sync",
          "Photo, signature, GPS, video evidence",
          "Conditional question logic",
          "Severity-based action workflows",
          "Compliance scoring and dashboards",
          "Multi-language support",
          "Integration with QMS, EHS, asset systems",
        ],
        microsoftStack:["Power Apps","Power Automate","Dynamics 365 Field Service","Power BI","Azure Storage"],
        benefits:[
          {v:"-78%",u:"",l:"Time to action"},
          {v:"Offline",u:"",l:"Field capability"},
          {v:"100%",u:"",l:"Photo evidence captured"},
          {v:"Real-time",u:"",l:"Findings visibility"},
        ],
        timeline:"4–6 weeks",
        outcomes:"Construction group equipped 240 inspectors with mobile audit apps — reducing inspection-to-action time from 14 to 3 days and improving close-out rates from 71% to 96%.",
        relevantIndustries:["manufacturing","logistics-supply-chain","retail-ecommerce"],
        relatedSolutions:["field-service-apps","enterprise-power-apps","workflow-automation"],
        process:[
          "Inspection process audit & checklist design (Week 1)",
          "Mobile UX design & question flow (Week 2)",
          "Build app & integrate backend (Weeks 3–4)",
          "Field pilot with inspectors (Week 5)",
          "Multi-site rollout (Week 6)",
        ],
        faq:[
          {q:"Will it work in factories / basements / remote sites with no signal?",a:"Yes — full offline mode. Inspectors complete inspections without connectivity; data syncs automatically when reconnecting. Photos buffer locally and upload when bandwidth available."},
          {q:"Can it integrate with our existing QMS / EHS platform?",a:"Yes — common integrations include Intelex, ETQ Reliance, Cority, custom QMS systems. Inspection findings flow into your authoritative QMS while our app provides the mobile capture experience."},
          {q:"What about regulator-format reports (HSE, OSHA, FDA)?",a:"Configurable report templates per regulator. We deploy templates aligned to HSE (UK), OSHA (US), FDA, MHRA, EHS Act (India), etc. PDF output ready for regulator submission."},
        ],
      },
      {
        slug:"asset-management", n:"Package", t:"Asset Management Systems",
        d:"Asset lifecycle, maintenance & tracking",
        hero:"Track equipment, fleet, IT assets, facilities — from acquisition through maintenance through disposal. Asset registers, preventive maintenance scheduling, work order management, depreciation tracking, integration with finance. Built on Dynamics 365 Field Service and Power Platform.",
        scenario:"You can't answer 'how many laptops do we have', 'when's the last service on truck 42', or 'what's our maintenance backlog' without spending an hour in spreadsheets. Assets disappear without trace; maintenance happens reactively; downtime costs spiral.",
        challenges:[
          "No single asset register — assets tracked in 4-5 systems",
          "Preventive maintenance scheduled inconsistently",
          "Work orders captured on paper or in emails",
          "Asset cost tracking can't tie to specific units",
        ],
        capabilities:[
          { n:"Layers",   t:"Unified asset register",   d:"All assets — IT, fleet, equipment, facilities — in one register with full lifecycle tracking." },
          { n:"Zap",      t:"Preventive maintenance",   d:"Risk-based maintenance scheduling: time-based, usage-based, condition-based with IoT integration." },
          { n:"Cpu",      t:"Mobile work orders",         d:"Technicians receive work orders on mobile — labour, parts, photos, signatures captured at point of work." },
          { n:"Chart",    t:"Total cost of ownership",   d:"Cost per asset: acquisition + maintenance + downtime + replacement — for capex planning." },
        ],
        features:[
          "Unified asset register (IT, fleet, equipment, facilities)",
          "QR/barcode/RFID asset tracking",
          "Preventive maintenance scheduling",
          "Mobile work order app for technicians",
          "Parts inventory management",
          "Vendor and warranty tracking",
          "Depreciation and TCO reporting",
          "Integration with finance, procurement, ERP",
        ],
        microsoftStack:["Dynamics 365 Field Service","Power Apps","Power Automate","Dynamics 365 Finance","Azure IoT Hub"],
        benefits:[
          {v:"100%",u:"",l:"Asset visibility"},
          {v:"-32%",u:"",l:"Reactive maintenance"},
          {v:"+24%",u:"",l:"Asset uptime"},
          {v:"TCO",u:"-tracked",l:"Per asset"},
        ],
        timeline:"6–10 weeks",
        outcomes:"Logistics fleet operator (1,400 vehicles) reduced reactive maintenance by 32% and improved asset uptime by 24% — saving £2.1M annually in maintenance cost and lost utilisation.",
        relevantIndustries:["logistics-supply-chain","manufacturing","retail-ecommerce"],
        relatedSolutions:["field-service-apps","operations-management","enterprise-power-apps"],
        process:[
          "Asset inventory audit & taxonomy (Weeks 1–2)",
          "Data model & integration design (Week 3)",
          "Build platform & migrate data (Weeks 4–7)",
          "Mobile app rollout to technicians (Weeks 8–9)",
          "Maintenance scheduling go-live (Week 10)",
        ],
        faq:[
          {q:"How does this compare to dedicated CMMS like Maximo or Infor EAM?",a:"For organisations with hundreds of millions of pounds of asset value or extremely complex maintenance regimes, dedicated CMMS still has edge. For mid-market and most enterprise asset estates, our Microsoft-based approach delivers equivalent capability at lower TCO."},
          {q:"Can it integrate with IoT sensors on equipment?",a:"Yes — Azure IoT Hub ingests sensor data; condition-based maintenance triggers when thresholds breach. We've deployed with vibration sensors on motors, current monitoring on pumps, temperature on cold chain assets."},
          {q:"What about fleet vehicles and telematics?",a:"Fleet management is well-supported — vehicle tracking, fuel monitoring, driver behaviour, service scheduling. We integrate with major telematics providers (Geotab, Verizon Connect, Webfleet) for real-time vehicle data."},
        ],
      },
      {
        slug:"service-request-platforms", n:"LifeBuoy", t:"Service Request Platforms",
        d:"Internal service request portals (IT, HR, facilities)",
        hero:"One front door for every internal service request — IT, HR, facilities, finance, procurement. Branded, mobile-friendly, AI-routed. Replace fragmented helpdesks and ticket systems with a unified service experience. Built on Power Apps with backend ServiceNow / Cherwell / Dynamics integration.",
        scenario:"Employees need a laptop replacement, a leave request, a desk move, an expense reimbursement, a new vendor setup — and each one goes through a different portal, mailbox, or form. They give up and ask their manager instead. Service teams are overwhelmed by 'wrong-channel' requests.",
        challenges:[
          "Employees navigate 5+ different portals/mailboxes for services",
          "Service teams overwhelmed by misrouted requests",
          "Service catalogue is inconsistent and undiscoverable",
          "Status of submitted requests is opaque",
        ],
        capabilities:[
          { n:"Globe",    t:"Unified service catalogue", d:"All internal services — IT, HR, facilities, finance — in one searchable catalogue with consistent UX." },
          { n:"Brain",    t:"AI request routing",          d:"Natural-language request capture; AI classifies and routes to the right service team with full context." },
          { n:"Layers",   t:"Backend integration",         d:"ServiceNow, Cherwell, Dynamics, custom systems — tickets created seamlessly with no front-end exposure." },
          { n:"Chart",    t:"Service analytics",            d:"SLA performance, satisfaction, request patterns — for service leaders to drive improvement." },
        ],
        features:[
          "Branded internal service portal",
          "AI-routed natural-language request capture",
          "Mobile-friendly service catalogue",
          "Integration with ServiceNow, Cherwell, Dynamics",
          "Real-time request status tracking",
          "SLA monitoring and escalation",
          "Service satisfaction surveys",
          "Service team performance dashboards",
        ],
        microsoftStack:["Power Apps","Power Pages","Power Automate","Azure OpenAI","Microsoft Teams"],
        benefits:[
          {v:"1 portal",u:"",l:"All services"},
          {v:"-55%",u:"",l:"Misrouted requests"},
          {v:"+42 pt",u:"",l:"Employee NPS"},
          {v:"Real-time",u:"",l:"Status visibility"},
        ],
        timeline:"5–8 weeks",
        outcomes:"Healthcare trust unified 11 service portals into one experience for 8,200 employees — reducing misrouted requests by 55% and improving employee service NPS by 42 points within 90 days.",
        relevantIndustries:["healthcare","professional-services","financial-services"],
        relatedSolutions:["internal-business-portals","enterprise-power-apps","customer-experience-apps"],
        process:[
          "Service catalogue audit & taxonomy (Weeks 1–2)",
          "UX design & request flow design (Week 3)",
          "Build portal & integrate backend systems (Weeks 4–6)",
          "Pilot with 2-3 service teams (Week 7)",
          "Org-wide launch & adoption (Week 8)",
        ],
        faq:[
          {q:"Will this replace our existing ServiceNow / Cherwell?",a:"No — we sit on top of your existing ITSM. Service teams continue to work in ServiceNow/Cherwell; employees experience a unified front door. Best of both worlds."},
          {q:"How does AI routing decide where to send requests?",a:"Trained on your service catalogue and historical ticket data. The AI classifies 'I can't connect to VPN' → IT > Network > VPN; 'I need parental leave' → HR > Leave > Parental. Accuracy improves with usage."},
          {q:"Can different services have different SLAs and approval flows?",a:"Yes — each service in the catalogue has its own SLA, approval matrix, and routing. Common to deploy 30–50 service types with distinct configurations."},
        ],
      },
    ],
  },


  /* ════════════ 4. ANALYTICS & INSIGHTS ════════════ */
  {
    slug:"analytics-insights", heading:"Analytics & Insights", icon:"Chart", color:C.green, bg:C.greenL,
    desc:"Dashboards, BI & AI-driven insights",
    tagline:"Turn your data into decisions — at the speed of business",
    overview:"Executive KPI dashboards, real-time operational reporting, AI-powered business insights, department-specific analytics — built on Microsoft Fabric and Power BI Premium. From single-team analytics to enterprise-wide BI platforms, with AI-narrated commentary and natural-language exploration.",
    catStats:[
      {v:"4",      l:"Analytics types"},
      {v:"100+",   l:"Dashboards deployed"},
      {v:"<6 wk",  l:"Average build time"},
      {v:"Real-time", l:"Refresh rates"},
    ],
    animVariant:"data",
    animAccent:"#a5b4fc",
    items:[
      {
        slug:"executive-kpi-dashboards", n:"Chart", t:"Executive KPI Dashboards", tag:"Most Popular",
        d:"Live KPI dashboards for the C-suite",
        hero:"Single-pane-of-glass executive dashboards: revenue, EBITDA, AUM, customer health, operations, talent — all in one place, refreshed daily, mobile-first, with AI-generated commentary explaining variance. Built on Microsoft Fabric with row-level security for the executive team.",
        scenario:"Your executives operate on different versions of the truth — finance presents one number, sales another, ops a third. Board prep takes finance team days each month. Live performance unavailable; executives operate on month-old data.",
        challenges:[
          "Different teams report different numbers — no single source of truth",
          "Board prep consumes 3+ days every month",
          "Executives can't access real-time KPIs on mobile",
          "Variance commentary is manual and inconsistent",
        ],
        capabilities:[
          { n:"Layers",   t:"Unified semantic model",   d:"One Fabric model defines metrics consistently — revenue, EBITDA, NIMs, AUM — same numbers everywhere." },
          { n:"Chart",    t:"Live executive dashboards", d:"Mobile-first dashboards with hourly to real-time refresh — KPI tiles, trends, drilldowns." },
          { n:"Brain",    t:"AI variance commentary",    d:"GPT-4 writes 'what changed, why, watch for' commentary — saving finance team's monthly narrative work." },
          { n:"Shield",   t:"Row-level security",        d:"Regional MDs see their region; group CFO sees everything — appropriate visibility, no over-sharing." },
        ],
        features:[
          "Executive KPI dashboard set (financial, operational, customer)",
          "Mobile-first responsive design",
          "AI-generated variance commentary",
          "Drilldown from KPI to root cause",
          "Scheduled morning briefing emails / Teams posts",
          "Anomaly detection and alerts",
          "Row-level security by role and region",
          "Annotations and goal tracking",
        ],
        microsoftStack:["Microsoft Fabric","Power BI Premium","Azure OpenAI","Power Automate","Microsoft Teams"],
        benefits:[
          {v:"-72%",u:"",l:"Board prep time"},
          {v:"Mobile",u:"-first",l:"Anywhere access"},
          {v:"AI",u:"-narrated",l:"Variance commentary"},
          {v:"Single",u:" truth",l:"No reconciliation"},
        ],
        timeline:"6–9 weeks",
        outcomes:"PE-backed mid-market group cut monthly board-pack preparation from 4 days to 6 hours — and executives now check live KPIs daily rather than waiting for monthly cycles.",
        relevantIndustries:["financial-services","manufacturing","retail-ecommerce"],
        relatedSolutions:["executive-dashboards","microsoft-fabric","enterprise-reporting"],
        process:[
          "Executive needs & KPI workshop (Weeks 1–2)",
          "Semantic model architecture (Weeks 3–4)",
          "Build Fabric & dashboards (Weeks 5–7)",
          "AI commentary tuning (Week 8)",
          "Executive rollout & training (Week 9)",
        ],
        faq:[
          {q:"How do we agree on KPI definitions across teams?",a:"This is the hardest part — and most important. Week 1 workshop with CFO, COO, CRO, CHRO to agree definitions, calculations, and ownership of each KPI. We document and version these as the 'metric dictionary' — a critical deliverable."},
          {q:"Can it pull from multiple ERPs / source systems?",a:"Yes — multi-source consolidation is standard. We routinely consolidate 5-10 source systems (ERPs, CRMs, HRIS, custom apps) into one Fabric model. Currency, entity, GAAP differences handled in the semantic layer."},
          {q:"How real-time is real-time?",a:"For financial KPIs: typically daily refresh (matches accounting reality). For operational/sales KPIs: hourly or sub-hourly. For e-commerce: near-realtime via streaming. We tune refresh by KPI based on business need vs. cost."},
        ],
      },
      {
        slug:"realtime-operational-reporting", n:"Zap", t:"Real-Time Operational Reporting",
        d:"Live operational dashboards for ground teams",
        hero:"Production lines, warehouses, contact centres, stores, fleets — front-line teams need real-time visibility, not weekly reports. We build live operational dashboards on Microsoft Fabric with sub-minute refresh, surfaced on shop-floor displays, supervisor tablets, and mobile apps.",
        scenario:"Your shift supervisors walk the floor blind — they don't see throughput, downtime, quality, or staffing in real time. By the time daily reports arrive, the shift is over. Issues compound through the day because no one sees them happening.",
        challenges:[
          "Operational reports arrive yesterday — too late to act",
          "Front-line supervisors have no live visibility",
          "Shop-floor displays show last week's data",
          "Anomalies caught hours or days after they occur",
        ],
        capabilities:[
          { n:"Globe",    t:"Live data pipelines",    d:"Azure Event Hubs / IoT Hub ingest streaming data; Fabric processes in seconds; dashboards refresh sub-minute." },
          { n:"Chart",    t:"Operational dashboards", d:"Shop floor, warehouse, contact centre, store dashboards — live OEE, throughput, queue, NPS." },
          { n:"Cpu",      t:"Andon-style displays",     d:"Wall-mounted shop-floor displays show live performance; visual cues for normal/warning/critical states." },
          { n:"Brain",    t:"Anomaly detection",        d:"ML detects unusual patterns (sudden throughput drop, queue spike, quality drift) — alerts supervisors instantly." },
        ],
        features:[
          "Live operational dashboards (sub-minute refresh)",
          "Shop-floor / warehouse / store displays",
          "Mobile supervisor app with live KPIs",
          "Streaming data ingestion from IoT / systems",
          "Anomaly detection with alerts",
          "Historical trend analysis on same data",
          "Multi-site rollup with site comparisons",
          "Integration with MES, WMS, contact centre platforms",
        ],
        microsoftStack:["Microsoft Fabric","Azure Event Hubs","Power BI Premium","Azure Machine Learning","Power Apps"],
        benefits:[
          {v:"<60s",u:"",l:"Data latency"},
          {v:"+18%",u:"",l:"Throughput improvement"},
          {v:"-34%",u:"",l:"Issue resolution time"},
          {v:"Live",u:"",l:"24/7 ops visibility"},
        ],
        timeline:"6–8 weeks",
        outcomes:"FMCG manufacturer deployed live OEE dashboards across 4 plants — increased throughput 18% within 6 months by enabling shift supervisors to spot and resolve issues in minutes rather than days.",
        relevantIndustries:["manufacturing","logistics-supply-chain","retail-ecommerce"],
        relatedSolutions:["microsoft-fabric","operations-management","predictive-insights"],
        process:[
          "Operational KPI workshop with front-line leaders (Week 1)",
          "Streaming architecture & data pipeline design (Weeks 2–3)",
          "Build pipelines & live dashboards (Weeks 4–6)",
          "Pilot at one site (Week 7)",
          "Multi-site rollout (Week 8)",
        ],
        faq:[
          {q:"What if our equipment doesn't have IoT sensors?",a:"We retrofit IoT (vibration, current, temperature) via Azure IoT Edge gateways. Most legacy machines can be sensorised for £5k-£15k per machine, with payback under 12 months from improved visibility."},
          {q:"Can it replace our existing MES / WMS dashboards?",a:"We overlay rather than replace. MES/WMS dashboards focus on transactions; our analytics layer focuses on insights, anomalies, and cross-system views. Many clients keep both."},
          {q:"What about contact centre / call queue real-time reporting?",a:"Yes — we integrate with major contact centre platforms (Genesys, NICE, Five9, Microsoft Teams CC) for live queue, agent status, wait times, NPS. Common deployment alongside Microsoft Teams Customer Service."},
        ],
      },
      {
        slug:"ai-business-insights", n:"Brain", t:"AI Business Insights",
        d:"Predictive models, forecasting & ML insights",
        hero:"Move from describing what happened to predicting what's next: demand forecasting, churn prediction, customer lifetime value, anomaly detection, supplier risk scoring. Production-grade ML on Microsoft Fabric and Azure ML — operationalised into dashboards, workflows, and decisions.",
        scenario:"Your data science team builds notebooks that never make it to production. Or you don't have data scientists and feel stuck with descriptive reporting only. Business decisions are made on instinct because predictive intelligence isn't actionable in your tools.",
        challenges:[
          "Data science notebooks never make it to production",
          "No internal ML capability; predictive intelligence absent",
          "Decisions made on intuition rather than forecast",
          "Existing tools show 'what happened', not 'what's next'",
        ],
        capabilities:[
          { n:"Brain",    t:"Production ML models",     d:"Azure ML pipelines deployed as managed endpoints — versioned, monitored, governed for compliance." },
          { n:"Chart",    t:"Embedded predictions",      d:"Forecasts and predictions surface in Power BI dashboards, workflows, mobile apps — where decisions happen." },
          { n:"Layers",   t:"Custom + foundation models", d:"Custom ML for narrow problems (churn, demand) + GPT-4 for unstructured insight (text mining, summarisation)." },
          { n:"Shield",   t:"Responsible AI",             d:"Model fairness, drift detection, explainability — for ML you can defend to auditors and regulators." },
        ],
        features:[
          "Custom ML models (demand, churn, risk, anomaly)",
          "Azure ML production deployment",
          "Model monitoring and drift detection",
          "Embedded predictions in Power BI",
          "Generative AI for unstructured analysis",
          "MLOps pipelines for retraining",
          "Explainability and fairness reports",
          "Integration with operational systems",
        ],
        microsoftStack:["Microsoft Fabric","Azure Machine Learning","Azure OpenAI","Power BI","Azure DevOps"],
        benefits:[
          {v:"+22%",u:"",l:"Forecast accuracy"},
          {v:"Production",u:"-ready",l:"MLOps governed"},
          {v:"Decisions",u:"",l:"On predictions"},
          {v:"Explainable",u:"",l:"AI outputs"},
        ],
        timeline:"8–14 weeks per use case",
        outcomes:"Retail group improved demand forecast accuracy by 22% — reducing stockouts 31% and excess inventory 18%, with £3.4M annual margin improvement from supply-chain optimisation.",
        relevantIndustries:["retail-ecommerce","financial-services","manufacturing"],
        relatedSolutions:["predictive-insights","microsoft-fabric","ai-ready-data"],
        process:[
          "Use-case prioritisation & ROI sizing (Week 1)",
          "Data quality assessment (Weeks 2–3)",
          "Feature engineering & model development (Weeks 4–8)",
          "Production deployment & monitoring (Weeks 9–11)",
          "Embedded operationalisation (Weeks 12–14)",
        ],
        faq:[
          {q:"We don't have a data science team — can we still do this?",a:"Yes — our team includes data scientists who build, deploy, and operationalise the models. We then transfer to your team for ongoing maintenance, or maintain on retainer if you prefer to focus internal team elsewhere."},
          {q:"How do you avoid the 'notebook never makes production' problem?",a:"By starting with the production architecture — Azure ML, MLOps pipelines, monitoring — and reverse-engineering the development. Models built from day one to be deployable, observable, retrainable."},
          {q:"What about EU AI Act / FCA / regulatory requirements on AI?",a:"Responsible AI is built in: model documentation, fairness reports, explainability, drift monitoring. We deploy models that meet EU AI Act high-risk requirements, FCA model risk management expectations, and GDPR Article 22."},
        ],
      },
      {
        slug:"department-analytics", n:"BarChart", t:"Department Analytics",
        d:"Function-specific dashboards (sales, HR, finance, ops)",
        hero:"Sales analytics, HR analytics, finance analytics, operations analytics, marketing analytics — purpose-built for each function's specific KPIs and workflows. Standardised on Microsoft Fabric and Power BI Premium, with function-specific data models and dashboards each team actually uses.",
        scenario:"Your teams ask for analytics — sales wants pipeline analytics, HR wants people analytics, marketing wants attribution — but you can't service all the requests. Each team builds shadow analytics in spreadsheets or buys their own SaaS tool. Data silos multiply.",
        challenges:[
          "Each department's analytics needs unmet by central BI team",
          "Teams build shadow analytics in spreadsheets or buy SaaS",
          "Data silos prevent cross-functional insight",
          "Inconsistent metric definitions across departments",
        ],
        capabilities:[
          { n:"Users",    t:"Department-specific apps", d:"Pre-built analytical apps for sales, HR, finance, marketing, operations — tailored to each function's KPIs." },
          { n:"Layers",   t:"Shared semantic model",     d:"Common data model with department-specific views — consistency across teams without duplication." },
          { n:"Brain",    t:"Self-service exploration",  d:"Department analysts explore on Power BI Premium without IT involvement — governed by row-level security." },
          { n:"Chart",    t:"Cross-functional insight",   d:"Same data powers sales analytics AND finance analytics — enabling cross-functional analysis when needed." },
        ],
        features:[
          "Sales analytics (pipeline, forecast, win-rate)",
          "HR analytics (attrition, hiring, comp, DEI)",
          "Finance analytics (P&L, cash, AR aging)",
          "Marketing analytics (attribution, MQL, campaign ROI)",
          "Operations analytics (throughput, quality, SLA)",
          "Self-service exploration for department analysts",
          "Cross-departmental data integration",
          "Mobile dashboards for each function",
        ],
        microsoftStack:["Microsoft Fabric","Power BI Premium","Dynamics 365","Azure Synapse Link","Power Automate"],
        benefits:[
          {v:"5",u:" depts",l:"Common platform"},
          {v:"Self-service",u:"",l:"For analysts"},
          {v:"Shared",u:" model",l:"No silos"},
          {v:"Cross",u:"-functional",l:"Insight enabled"},
        ],
        timeline:"4–6 weeks per department",
        outcomes:"PE-backed firm consolidated 9 departmental analytics tools (Tableau, Power BI sprawl, Excel) onto unified Microsoft Fabric platform — saving £640k/year in tool costs while improving analytical depth across all departments.",
        relevantIndustries:["financial-services","professional-services","retail-ecommerce"],
        relatedSolutions:["microsoft-fabric","executive-dashboards","unified-data-platforms"],
        process:[
          "Department analytics audit & prioritisation (Week 1)",
          "Shared semantic model design (Week 2)",
          "Build core department dashboards (Weeks 3–4)",
          "Pilot with department analysts (Week 5)",
          "Refine, train & rollout (Week 6)",
        ],
        faq:[
          {q:"How is this different from giving each department Power BI?",a:"Critical difference: shared semantic model means consistent metrics across departments. Sales 'revenue' = Finance 'revenue' = Board 'revenue'. Without shared model, you get the metric reconciliation chaos you're trying to escape."},
          {q:"Can departments still have their own analysts and analyses?",a:"Yes — and encouraged. Self-service exploration is part of the design. Departments analyse, dashboard, model on top of the shared data. They contribute additions back to the shared model when broadly useful."},
          {q:"What if we want to consolidate other BI tools (Tableau, Qlik, Looker)?",a:"Common consolidation. We migrate dashboards from Tableau/Qlik/Looker to Power BI Premium with parity. Most clients see 50-70% licence cost reduction plus improved Microsoft ecosystem integration."},
        ],
      },
    ],
  },


  /* ════════════ 5. MODERN WORKPLACE SOLUTIONS ════════════ */
  {
    slug:"modern-workplace", heading:"Modern Workplace Solutions", icon:"Users", color:C.orange, bg:C.orangeL,
    desc:"Intranet, Teams, Viva & document management",
    tagline:"Where work happens — modernised, unified, secure",
    overview:"Intranet portals, employee experience platforms, Teams collaboration systems, document management — the digital fabric of modern work. Built on Microsoft 365 (SharePoint, Teams, Viva), branded for your organisation, mobile-first, secure. Where 90% of your employees' working day actually happens.",
    catStats:[
      {v:"4",      l:"Workplace solutions"},
      {v:"200k+",  l:"Employees supported"},
      {v:"<10 wk", l:"Average time to live"},
      {v:"M365",   l:"Native integration"},
    ],
    animVariant:"workplace",
    animAccent:"#7dd3fc",
    items:[
      {
        slug:"intranet-portals", n:"Globe", t:"Intranet Portals", tag:"Most Popular",
        d:"Modern branded intranets on SharePoint",
        hero:"Replace stale intranets that no one visits with modern, branded, mobile-first portals built on SharePoint Online and Microsoft Viva. AI-powered search, personalised content, integrated services, real engagement analytics. From legacy 'company intranet' to active employee hub.",
        scenario:"Your intranet was built years ago, looks dated, has broken navigation, and no one visits except for the link to expenses. Employees can't find policies, news goes unread, leadership communications fall flat, and the platform is a liability rather than asset.",
        challenges:[
          "Legacy intranet looks dated; employees don't visit",
          "Search is broken; finding policies/forms is impossible",
          "No personalisation — every employee sees same homepage",
          "Mobile experience is afterthought or absent",
        ],
        capabilities:[
          { n:"Globe",    t:"Branded modern portal",    d:"Designed for your brand — colours, typography, imagery, navigation — feeling premium and current." },
          { n:"Brain",    t:"AI-powered search",          d:"Azure AI Search across all content — policies, documents, services, people — with cited natural-language answers." },
          { n:"Users",    t:"Personalisation",            d:"Each employee sees role-relevant content, news, services — driven by HRIS, role, location, interests." },
          { n:"Chart",    t:"Engagement analytics",       d:"Real engagement data — what content works, what doesn't, who's reaching whom — for comms teams." },
        ],
        features:[
          "Modern branded SharePoint Online portal",
          "Mobile-first responsive design",
          "AI-powered enterprise search",
          "Personalised homepage by role / location",
          "Internal news with leadership broadcasts",
          "Self-service forms and request capture",
          "Employee directory with skills",
          "Real-time engagement analytics",
        ],
        microsoftStack:["SharePoint Online","Microsoft Viva Connections","Azure AI Search","Microsoft Entra ID","Power Apps"],
        benefits:[
          {v:"+285%",u:"",l:"Intranet visits"},
          {v:"-58%",u:"",l:"'Where do I find' tickets"},
          {v:"Mobile",u:"-first",l:"Anywhere access"},
          {v:"4 hrs",u:"/week",l:"Saved per employee"},
        ],
        timeline:"8–12 weeks",
        outcomes:"NHS trust replaced legacy intranet for 12,000 employees — 285% visit increase in 90 days and 58% reduction in 'where do I find' helpdesk tickets, recovering ~32k staff hours annually.",
        relevantIndustries:["healthcare","professional-services","financial-services"],
        relatedSolutions:["sharepoint-modernisation","internal-business-portals","ai-knowledge-assistants"],
        process:[
          "Strategy workshop & employee research (Weeks 1–2)",
          "Information architecture & design (Weeks 3–4)",
          "Build portal & integrate services (Weeks 5–9)",
          "Content migration & pilot (Weeks 10–11)",
          "Org-wide launch & adoption campaign (Week 12)",
        ],
        faq:[
          {q:"Do we need to migrate from our legacy intranet (LumApps, Workplace, custom)?",a:"Content migration is part of every project. We assess what to migrate vs archive vs deprecate, then automate the migration. Most clients migrate 20-40% of content and archive the rest."},
          {q:"How do we drive adoption?",a:"Adoption is designed in: executive sponsorship, content owner accountability, embedded into M365 (no separate login), mobile-first, and Viva Connections card in Teams. Without these, adoption fails."},
          {q:"Can we make it as good as our public marketing website?",a:"Yes — we deliver design quality matching your public web presence. Same designers, same brand standards, just inside SharePoint. Most employees should not be able to tell it's SharePoint."},
        ],
      },
      {
        slug:"employee-experience-platforms", n:"Heart", t:"Employee Experience Platforms",
        d:"Microsoft Viva deployments for engagement",
        hero:"Microsoft Viva turns Microsoft Teams into a complete employee experience platform — learning, recognition, communications, wellbeing, knowledge, goals. We deploy Viva Suite end-to-end: planning, configuration, content strategy, change management. The future of employee experience without bolt-on tools.",
        scenario:"Employee experience tools fragment: Workday Learn, Cornerstone, separate engagement tool, recognition platform, wellbeing app. Each requires login, integration, separate adoption push. Most go underused. M365 already supports it all natively through Viva — but you haven't deployed it.",
        challenges:[
          "Fragmented EX tools — learning, comms, wellbeing in separate platforms",
          "Each tool requires separate adoption and integration",
          "ROI of EX investment hard to demonstrate",
          "Viva entitlements unused because no one knows where to start",
        ],
        capabilities:[
          { n:"BookOpen", t:"Viva Learning",            d:"Curated and custom learning paths surfaced in Teams — LinkedIn Learning + your own + external partners unified." },
          { n:"Megaphone",t:"Viva Connections",          d:"Branded employee experience inside Teams — news, recognition, services, voice surveys." },
          { n:"Heart",    t:"Viva Insights & Pulse",     d:"Wellbeing insights for managers and individuals; lightweight pulse surveys for engagement." },
          { n:"BookOpen", t:"Viva Topics & Engage",      d:"Auto-organised knowledge topics; community-based Viva Engage for cross-functional networking." },
        ],
        features:[
          "Viva Connections (employee experience hub)",
          "Viva Learning (LinkedIn Learning + custom + 3rd party)",
          "Viva Insights (manager + employee wellbeing)",
          "Viva Topics (AI-organised knowledge)",
          "Viva Pulse (lightweight engagement surveys)",
          "Viva Goals (OKR management)",
          "Viva Engage (community / Yammer replacement)",
          "Change management & adoption programme",
        ],
        microsoftStack:["Microsoft Viva Suite","Microsoft Teams","SharePoint Online","Microsoft Entra ID","Power BI"],
        benefits:[
          {v:"+38 pt",u:"",l:"eNPS improvement"},
          {v:"-£90/yr",u:"/empl",l:"EX tool consolidation"},
          {v:"M365",u:" native",l:"No extra login"},
          {v:"Manager",u:" insight",l:"Team wellbeing"},
        ],
        timeline:"8–14 weeks",
        outcomes:"Insurance group deployed full Viva Suite to 6,400 employees — replacing 4 EX tools, improving eNPS by 38 points, and saving £576k/year in licence consolidation.",
        relevantIndustries:["financial-services","professional-services","healthcare"],
        relatedSolutions:["internal-business-portals","employee-self-service","collaboration-automation"],
        process:[
          "EX strategy workshop (Weeks 1–2)",
          "Viva module roadmap & prioritisation (Week 3)",
          "Deploy Viva Connections & Learning (Weeks 4–8)",
          "Deploy Insights, Topics, Engage (Weeks 9–12)",
          "Change management & adoption (Weeks 13–14)",
        ],
        faq:[
          {q:"Is Viva worth deploying — isn't it just Microsoft marketing?",a:"Viva is a real product suite, not marketing. When deployed properly with change management, it delivers measurable engagement, learning, and wellbeing outcomes. But deployment matters — half-deployed Viva looks like Microsoft marketing."},
          {q:"How does it compare to Workday Learning, Cornerstone, Glint?",a:"Function for function, Viva matches or exceeds these dedicated platforms while being included in M365 E5. Cost of consolidation is typically £80-120/employee/year. For organisations on E5, Viva is often a no-brainer."},
          {q:"What about adoption — won't it be just another tool?",a:"Critical insight: Viva isn't 'another tool' — it lives inside Teams, where employees already work. Connect to existing daily workflow rather than separate destination. Adoption typically 5x higher than standalone EX tools."},
        ],
      },
      {
        slug:"teams-collaboration", n:"Users", t:"Teams Collaboration Systems",
        d:"Microsoft Teams optimisation & governance",
        hero:"Microsoft Teams is the modern workplace's heart — but most organisations under-use it. We deploy and optimise Teams for your specific work styles: governance, app integrations, custom Teams apps, meeting rooms, voice, contact centre. From Teams basics to power user enablement.",
        scenario:"Your Teams deployment grew organically — channels everywhere, no governance, file management chaotic, meetings have audio issues, Teams isn't integrated with your business systems, and most users use 10% of capability. Teams sprawl and licence waste are real costs.",
        challenges:[
          "Teams sprawl — every project gets new team, governance absent",
          "Files scattered across SharePoint, OneDrive, Teams without strategy",
          "Meeting rooms not optimised for hybrid work",
          "Teams not integrated with business systems",
        ],
        capabilities:[
          { n:"Shield",   t:"Teams governance",        d:"Templates, naming conventions, lifecycle policies, expiration — preventing sprawl while enabling self-service." },
          { n:"Layers",   t:"Business integrations",   d:"D365, Salesforce, ServiceNow, custom apps surfaced in Teams — work where work happens." },
          { n:"Video",    t:"Meeting room modernisation", d:"Teams Rooms hardware design, deployment, management — finally fixing 'wait can you hear me?' meetings." },
          { n:"Phone",    t:"Teams Voice & contact centre", d:"Replace legacy phone systems with Teams Phone; native or partner contact centre on Teams." },
        ],
        features:[
          "Teams governance and lifecycle management",
          "Custom Teams apps and business integrations",
          "Teams Rooms hardware design and deployment",
          "Teams Phone migration from legacy",
          "Teams contact centre setup",
          "Power user training and enablement",
          "External collaboration security and compliance",
          "Teams usage analytics and adoption",
        ],
        microsoftStack:["Microsoft Teams","Teams Phone","Teams Rooms","SharePoint Online","Microsoft Entra ID"],
        benefits:[
          {v:"Governed",u:"",l:"Teams sprawl"},
          {v:"-£40/yr",u:"/empl",l:"Phone consolidation"},
          {v:"Hybrid",u:"-ready",l:"Meeting rooms"},
          {v:"Integrated",u:"",l:"Business systems"},
        ],
        timeline:"6–12 weeks",
        outcomes:"Professional services firm consolidated Cisco phone system, separate web conferencing, and 12 meeting room platforms onto Teams — saving £840k/year while improving meeting quality and adoption.",
        relevantIndustries:["professional-services","financial-services","retail-ecommerce"],
        relatedSolutions:["teams-integrations","collaboration-automation","sharepoint-modernisation"],
        process:[
          "Teams maturity assessment (Weeks 1–2)",
          "Governance & roadmap design (Week 3)",
          "Deploy governance & integrations (Weeks 4–7)",
          "Teams Rooms & Voice (Weeks 8–11)",
          "Adoption & change management (Week 12)",
        ],
        faq:[
          {q:"What about Teams vs Slack — should we have a strong opinion?",a:"For Microsoft 365 organisations, Teams almost always wins on TCO and integration depth. For organisations heavily invested in Slack ecosystem, transition cost may not be worth it. We assess honestly."},
          {q:"How do you handle external collaboration safely?",a:"Microsoft Entra External ID + Teams external access policies. Different rules per partner type. Common patterns: full external access for clients/customers, channel-level access for vendors, restricted for sensitive matters."},
          {q:"Can we keep our existing AV in meeting rooms?",a:"Often yes — we audit existing hardware for Teams Rooms compatibility. Many existing displays, cameras, mics can be reused with new compute. Where new hardware needed, we design for your specific room geometries."},
        ],
      },
      {
        slug:"document-management", n:"FileText", t:"Document Management",
        d:"Enterprise DM on SharePoint with governance",
        hero:"Replace network drives, scattered shared folders, and ungoverned SharePoint with proper enterprise document management. Information architecture, retention policies, sensitivity labels, AI-powered classification, records management — built on SharePoint Online and Microsoft Purview.",
        scenario:"Documents live in 50 different places: network drives, OneDrive, Teams, SharePoint, email attachments, ECM systems. Critical documents can't be found. Retention policies aren't followed. Audit prep means digging through binders and folders for weeks. Compliance is theatre.",
        challenges:[
          "Documents scattered across drives, OneDrive, Teams, SharePoint",
          "No retention policies — old documents pile up forever",
          "Sensitive documents unencrypted and unprotected",
          "Records management non-existent or unenforced",
        ],
        capabilities:[
          { n:"Layers",   t:"Information architecture",  d:"Designed taxonomy: site hierarchy, content types, metadata — making content findable and governable." },
          { n:"Shield",   t:"Sensitivity & DLP",           d:"Microsoft Purview sensitivity labels; data loss prevention; encryption applied automatically by content type." },
          { n:"Brain",    t:"AI auto-classification",     d:"Azure AI classifies sensitivity, content type, retention — at the moment of upload, not retrospectively." },
          { n:"FileText", t:"Records management",          d:"Retention schedules, immutable records, defensible disposal — meeting SOX, GDPR, FOI, MoPI requirements." },
        ],
        features:[
          "Information architecture and site hierarchy design",
          "Microsoft Purview sensitivity labelling",
          "Data loss prevention (DLP) policies",
          "Records management and retention",
          "AI-powered auto-classification",
          "Migration from network drives and legacy DM",
          "External sharing governance",
          "eDiscovery and Compliance Manager",
        ],
        microsoftStack:["SharePoint Online","Microsoft Purview","Azure AI Document Intelligence","Microsoft Entra ID","Microsoft 365 Compliance"],
        benefits:[
          {v:"Findable",u:"",l:"All documents"},
          {v:"Auto",u:"-classified",l:"Sensitivity & retention"},
          {v:"GDPR",u:" ready",l:"Audit-defensible"},
          {v:"-£25/empl",u:"/yr",l:"Storage cost"},
        ],
        timeline:"10–16 weeks",
        outcomes:"Government agency consolidated 38 network drives and 4 ECM systems onto SharePoint Online with Purview governance — passing ICO audit with zero findings and saving £1.2M/year in legacy ECM licences.",
        relevantIndustries:["financial-services","healthcare","professional-services"],
        relatedSolutions:["document-management","sharepoint-modernisation","ai-document-intelligence"],
        process:[
          "Document estate audit & taxonomy design (Weeks 1–3)",
          "Information architecture & governance design (Weeks 4–5)",
          "Build & configure Purview / SharePoint (Weeks 6–9)",
          "Migration from legacy systems (Weeks 10–14)",
          "Records management & adoption (Weeks 15–16)",
        ],
        faq:[
          {q:"How do we migrate millions of documents from network drives?",a:"Phased migration with automated tools (ShareGate, AvePoint). Typical: 100GB-1TB per week per migration stream. Most large clients run 4-8 parallel streams. We migrate, classify, and tag as we go — not as separate phases."},
          {q:"What about specialised DM (iManage, NetDocuments) for legal / financial services?",a:"For deeply specialised matter management (law firms), iManage often remains. For general DM, SharePoint matches or exceeds at lower cost. We've migrated organisations from iManage to SharePoint successfully where the deep matter management features aren't needed."},
          {q:"Do we really need records management?",a:"For regulated industries (financial services, healthcare, government), yes — non-negotiable. For others, light-touch records management (key retention rules) avoids future legal/regulatory exposure. Cost of records management is far less than cost of being unable to defend disposal in litigation."},
        ],
      },
    ],
  },

]
